import {defineStore} from 'pinia'
import axios from 'axios'
import Cookies from 'js-cookie'

export const useProductListStore = defineStore('productList', {
    state: () => ({
        ecommerce: window.ecommerce,
        apiEndpoint: window.app.product_list.api_endpoint,
        layout: window.app.product_list.layout,
        hideSideBar: window.app.product_list.hideSideBar === "1" ? "1" : "0",
        tpgSideBarNavigation: window.app.product_list.tpgSideBarNavigation === "1" ? "1" : "0",
        apiSite: window.app.product_list.api_site,
        showComparelistButton: window.app.product_list.showComparelistButton,
        items: [],
        categoryCardsRaw: window?.app?.product_list?.categoryCards ?? [],
        locale: window.app.product_list.siteLanguage,
        itemrepoLocale: window.app.product_list.itemrepoLocale
            || window.app.product_list.siteLanguageHrefLang.replace('-', '_'),
        findAttributes: window.app.product_list.findAttributes,
        detailPageUrl: window.app.product_list.detailPageUrl,
        showPrice: window.app.product_list.showPrice,
        client: window.app.product_list.client,
        cartEnabled: window.app.product_list.cartEnabled === "1" ? "1" : "0",
        showBuyButton: window.app.product_list.enabledBuy,
        showButtonStyle: window.app.product_list.showButtonStyle,
        bazaarvoiceEnabled: window.app.product_list.bazaarvoiceEnabled,
        ratingEnabled: window.app.product_list.ratingEnabled,
        showBuyButtonOnThisWidget: window.app.product_list.showBuyButton,
        wantsAvailabilityPreFilter: window.app?.product_list?.prefilterAvailability === "1" ? "1" : "0",
        shopEnabled: window.app.product_list.shopEnabled,
        loaded: false,
        syliusLoaded: false,
        numberOfTotalItems: 9,
        isEmpty: false,
        pageNumber: 0,
        pageSize: 9,
        totalPages: 10,
        showPopup: false,
        showErrorContent: false,
        isEmptyFilterResult: false,
        initialFilterPending: false
    }),

    getters: {
        apiUrlGet: (state) => {
            return state.apiEndpoint + '/v2/item/find'
        },
        categoryCards(state) {
            return Array.isArray(state.categoryCardsRaw) ? state.categoryCardsRaw : []
        }
    },

    actions: {
        setInitialFilterPending(payload) {
            this.initialFilterPending = !!payload
        }, setLoaded(payload) {
            this.loaded = payload
        }, setIsEmptyFilterResult(payload) {
            this.isEmptyFilterResult = payload
        }, setSyliusLoaded(payload) {
            this.syliusLoaded = payload
        }, setItems(payload) {
            this.items = [...payload]
        }, addItems(payload) {
            this.items = [...this.items, ...payload]
        }, setPageSize(payload) {
            this.pageSize = payload
        }, setNumberOfTotalItems(payload) {
            this.numberOfTotalItems = payload
        }, setPageNumber(payload) {
            this.pageNumber = payload
        }, setTotalPages(payload) {
            this.totalPages = payload
        }, setIsEmpty(payload) {
            this.isEmpty = payload
        }, setShowPopup(payload) {
            this.showPopup = payload
        }, setShowErrorContent(payload) {
            this.showErrorContent = payload
        }, setURLParams() {
            const url = new URL(window.location.href)
            url.searchParams.set('pageNumber', this.pageNumber + 1)
            if (this.tpgSideBarNavigation === "1" && this.wantsAvailabilityPreFilter === "1") url.searchParams.set('onlyAvailable', 'true')
            window.history.pushState({}, '', url)
        }, setPredefinedFilter() {
            const url = new URL(window.location.href)
            if (url.searchParams.has('pageNumber')) {
                if (url.searchParams.get('pageNumber') == 0) {
                    this.pageNumber = 0
                } else {
                    this.pageNumber = parseInt(url.searchParams.get('pageNumber') - 1)
                }

            }
        }, async resolveClassificationsToItemNumbers() {
            const tpgCodes = (this.findAttributes.classifications || '')
                .split(',')
                .map(s => s.trim())
                .filter(Boolean)
            if (tpgCodes.length === 0) return

            try {
                const {data} = await axios.post(
                    `${this.apiEndpoint}/v2/website/filter`,
                    {
                        website: this.apiSite,
                        locale: this.itemrepoLocale,
                        tpgCodes,
                        filter: []
                    },
                    {timeout: 5000}
                )
                const resolved = Array.isArray(data?.itemNos) ? data.itemNos : []
                if (resolved.length > 0) {
                    this.findAttributes.itemNumbers = resolved.join(',')
                }
                delete this.findAttributes.classifications
            } catch (e) {
                // keep classifications-fallback in URL on failure
            }
        }, async get() {
            if (this.initialFilterPending) return

            this.setLoaded(false)
            this.setSyliusLoaded(false)

            // TPG view does not run the filter store, so expand the category TPG codes
            // here via /v2/website/filter (same endpoint the filter store uses).
            if ((this.tpgSideBarNavigation === "1" || this.hideSideBar === "1") && this.findAttributes.classifications) {
                await this.resolveClassificationsToItemNumbers()
            }

            const url = new URL(this.apiUrlGet)

            // Prefer itemNos; if they exist, skip classifications
            if (this.findAttributes.itemNumbers) {
                url.searchParams.set('itemNos', this.findAttributes.itemNumbers)
            } else if (this.findAttributes.classifications) {
                url.searchParams.set('classifications', this.findAttributes.classifications)
            }

            if (this.findAttributes.brand) url.searchParams.set('brand', this.findAttributes.brand)
            if (this.findAttributes.productFamily) url.searchParams.set('productFamily', this.findAttributes.productFamily)
            if (this.findAttributes.division) url.searchParams.set('division', this.findAttributes.division)
            if (this.locale) url.searchParams.set('locale', this.itemrepoLocale)
            if (this.apiSite) url.searchParams.set('site', this.apiSite)
            if (this.tpgSideBarNavigation === "1" && this.wantsAvailabilityPreFilter === "1") url.searchParams.set('onlyAvailable', 'true')

            url.searchParams.set('pageSize', this.pageSize)
            url.searchParams.set('pageNumber', this.pageNumber)

            try {
                const response = await axios.get(url, {timeout: 5000})
                this.setItems(response.data.content)
                this.setIsEmpty(response.data.empty)
                const hasItems = Array.isArray(response.data.content) && response.data.content.length > 0
                this.setIsEmptyFilterResult(!hasItems)
                this.setTotalPages(response.data.totalPages)
                this.setLoaded(true)
            } catch (error) {
                this.setShowErrorContent(true)
            }
            this.setURLParams()
        }, handleSyliusCall() {
            const syliusObj = window.ecommerce.ecommerce
            syliusObj.isProductInSylius()

            setTimeout(() => {
                this.setSyliusLoaded(true)
                this.pushItemsToDatalayer()
            }, 1000)
        }, pushItemsToDatalayer() {
            if (this.showBuyButton === '1' && typeof dataLayer !== 'undefined') {
                let itemsArray = []
                this.items.forEach((item, index) => {
                    if (item.price && item.price.price) {
                        let price = document.querySelector('.card__card[data-itemno="' + item.itemNo + '"] .price__price').getAttribute('data-price')
                        let obj = {
                            item_id: item.itemNo,
                            item_name: item.description,
                            item_brand: item.brand,
                            item_category: item.tpgCode,
                            index: index,
                            price: price,
                        }
                        itemsArray.push(obj)
                    }
                })
                dataLayer.push({ecommerce: null}) // Clear the previous ecommerce object.
                dataLayer.push({
                    event: 'view_item_list', ecommerce: {
                        item_list_id: window.location.pathname,
                        item_list_name: window.app.product_list.itemlistname,
                        items: itemsArray
                    }
                })
            }
        }, updateCurrentPageNumber(payload) {
            this.setPageNumber(payload)
            this.get().then(() => {})
        }, updateShowPopup(payload) {
            this.setShowPopup(payload)
        }, addToCompareList(payload) {
            let cookie = Cookies.get('clid')
            if (cookie) {
                let ascii = atob(cookie)
                if (ascii.indexOf(payload) === -1) {
                    let newString = ascii + ',' + payload
                    Cookies.set('clid', btoa(newString), {expires: 7})
                }
            } else {
                Cookies.set('clid', btoa(payload), {expires: 7})
            }
            this.setShowPopup(true)
        }
    }
})
